// V2 admin calls (BRIEFS C4.4). They use the OLD admin axios instance (api/axios.js): the
// admin area keeps its normal login (SPEC §5), and require_admin accepts that token.
import api from './axios';

const A = '/api/v2/admin';
const d = (p) => p.then((r) => r.data);

export const v2AdminChapters = () => d(api.get(`${A}/chapters`));
export const v2SetRelease = (chapterId, released) => d(api.post(`${A}/chapters/${chapterId}/release`, { released }));
export const v2SetStatus = (chapterId, status) => d(api.post(`${A}/chapters/${chapterId}/status`, { notes_status: status }));

export const v2NotesDryRun = (file) => d(api.post(`${A}/notes/dry-run`, file));
export const v2NotesApply = (file) => d(api.post(`${A}/notes/apply`, file));

// A chapter's notes as Harsh sees them, drafts included (admin preview).
export const v2ChapterNotesPreview = (chapterId) => d(api.get(`/api/v2/chapters/${chapterId}/notes`, { params: { preview: 1 } }));
export const v2GetPoint = (code) => d(api.get(`${A}/points/${encodeURIComponent(code)}`));
export const v2SavePoint = (code, body) => d(api.put(`${A}/points/${encodeURIComponent(code)}`, body));
export const v2PreviewPoint = (html) => d(api.post(`${A}/points/preview`, { html }));

export const v2ModuleMap = (chapterId) => d(api.get(`${A}/chapters/${chapterId}/module-map`));
export const v2SetModuleUnits = (chapterId, moduleCode, units) =>
  d(api.put(`${A}/chapters/${chapterId}/module-map/${encodeURIComponent(moduleCode)}`, { units }));

// Dictionary (C5.2)
export const v2DictList = (params) => d(api.get(`${A}/dictionary`, { params }));
export const v2DictSave = (id, body) => d(api.put(`${A}/dictionary/${id}`, body));
export const v2DictBulk = (entries, dryRun) => d(api.post(`${A}/dictionary/bulk`, entries, { params: dryRun ? { dry_run: 1 } : {} }));

// Video clips (C6.1)
export const v2Clips = (params) => d(api.get(`${A}/clips`, { params }));
export const v2ClipCreate = (body) => d(api.post(`${A}/clips`, body));
export const v2ClipUpdate = (id, body) => d(api.put(`${A}/clips/${id}`, body));
export const v2ClipUnits = (params) => d(api.get(`${A}/clips/units`, { params }));
export const v2ClipUnit = (code) => d(api.get(`${A}/clips/unit/${encodeURIComponent(code)}`));
export const v2ClipOrder = (code, clipIds) => d(api.put(`${A}/clips/unit/${encodeURIComponent(code)}/order`, { clip_ids: clipIds }));

// Videos from the owner's table (C11.9)
export const v2VideoImport = (text, dryRun) => d(api.post(`${A}/videos/import`, { text, dry_run: !!dryRun }));
export const v2VideoImportFile = (file, dryRun) => {
  const fd = new FormData();
  fd.append('file', file);
  return d(api.post(`${A}/videos/import-file`, fd, { params: { dry_run: dryRun ? 1 : 0 } }));
};
export const v2VideoPropose = (body) => d(api.post(`${A}/videos/propose`, body || {}));
export const v2VideoJobs = () => d(api.get(`${A}/videos/jobs`));
export const v2VideoReview = (status) => d(api.get(`${A}/videos/review`, { params: { status } }));
export const v2VideoApprove = (id, units, kind) => d(api.post(`${A}/videos/${id}/approve`, kind ? { units, kind } : { units }));
export const v2VideoReject = (id) => d(api.post(`${A}/videos/${id}/reject`));

// Question links (C7.1)
export const v2Links = (params) => d(api.get(`${A}/links`, { params }));
export const v2LinksPropose = (body) => d(api.post(`${A}/links/propose`, body));
export const v2LinksJobs = (chapterId) => d(api.get(`${A}/links/jobs`, { params: { chapter_id: chapterId } }));
export const v2LinksAuto = () => d(api.get(`${A}/links/auto`));
export const v2LinkConfirm = (questionId, pointCode) => d(api.post(`${A}/links/confirm`, { question_id: questionId, point_code: pointCode }));
export const v2LinkNoMatch = (questionId) => d(api.post(`${A}/links/no-match`, { question_id: questionId }));
export const v2LinksConfirmAll = (chapterId, minConfidence) => d(api.post(`${A}/links/confirm-all`, { chapter_id: chapterId, min_confidence: minConfidence }));
export const v2LinkPoints = (chapterId, q) => d(api.get(`${A}/links/points`, { params: { chapter_id: chapterId, q } }));

export const errText = (e, fallback = 'Something went wrong.') => {
  const det = e && e.response && e.response.data && e.response.data.detail;
  if (typeof det === 'string') return det;
  if (Array.isArray(det) && det[0] && det[0].msg) return det[0].msg;
  return fallback;
};

// One-liners (C8.1)
export const v2OneLiners = (params) => d(api.get(`${A}/one-liners`, { params }));

// Activity, reports, devices, settings (C3.4)
export const v2Students = () => d(api.get(`${A}/students`));
export const v2Activity = (params) => d(api.get(`${A}/activity`, { params }));
export const v2ActivityDays = (student) => d(api.get(`${A}/activity/days`, { params: { student } }));
export const v2Reports = (student) => d(api.get(`${A}/reports`, { params: student ? { student } : {} }));
export const v2ReportHtml = (id) => d(api.get(`${A}/reports/${id}`, { responseType: 'text' }));
export const v2ReportPreview = (studentId, day) => d(api.post(`${A}/reports/preview`, { student_id: studentId, day }, { responseType: 'text' }));
export const v2ReportSend = (studentId, day) => d(api.post(`${A}/reports/send`, { student_id: studentId, day }));
export const v2Devices = () => d(api.get(`${A}/devices`));
export const v2DeviceRevoke = (id) => d(api.post(`${A}/devices/${id}/revoke`));
export const v2Settings = (student) => d(api.get(`${A}/settings`, { params: student ? { student } : {} }));
export const v2SettingSave = (studentId, key, value) => d(api.put(`${A}/settings`, { student_id: studentId || null, key, value }));
export const v2SettingClear = (studentId, key) => d(api.delete(`${A}/settings`, { params: { student: studentId, key } }));

// India date (YYYY-MM-DD) for "today", the same day the server uses.
export const istToday = () => {
  const ist = new Date(Date.now() + (5.5 * 60 + new Date().getTimezoneOffset()) * 60000);
  const p = (n) => String(n).padStart(2, '0');
  return `${ist.getFullYear()}-${p(ist.getMonth() + 1)}-${p(ist.getDate())}`;
};
export const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

// PYQ tags (C11.4): view per chapter, remove one tag (is_active = false).
export const v2PyqList = (params) => d(api.get(`${A}/pyq`, { params }));
export const v2PyqRemove = (id) => d(api.post(`${A}/pyq/${id}/remove`));

// Printable views (C11.3): any list of question IDs, one V2 test / revision (any status),
// and the kind of each V2 row in a student's tests list.
export const v2PrintQuestions = (ids) => d(api.get(`${A}/print-questions`, { params: { ids } }));
export const v2TestReview = (testId) => d(api.get(`${A}/tests/${encodeURIComponent(testId)}/review`));
export const v2TestKinds = (studentId) => d(api.get(`${A}/students/${encodeURIComponent(studentId)}/test-kinds`));

// Notes (by chapter) — fixed NCERT structure + one section at a time (C11.7).
export const v2Structure = (chapterId) => d(api.get(`${A}/chapters/${chapterId}/structure`));
export const v2AddSection = (chapterId, title, after) => d(api.post(`${A}/chapters/${chapterId}/sections`, { title, after }));
export const v2EditSection = (code, body) => d(api.put(`${A}/sections/${encodeURIComponent(code)}`, body));
export const v2SectionPoints = (code) => d(api.get(`${A}/sections/${encodeURIComponent(code)}/points`));
export const v2SectionDryRun = (code, body) => d(api.post(`${A}/sections/${encodeURIComponent(code)}/notes/dry-run`, body));
export const v2SectionApply = (code, body) => d(api.post(`${A}/sections/${encodeURIComponent(code)}/notes/apply`, body));
export const v2ChapterNotesDryRun = (chapterId, file) => d(api.post(`${A}/chapters/${chapterId}/notes/dry-run`, file));
export const v2ChapterNotesApply = (chapterId, file) => d(api.post(`${A}/chapters/${chapterId}/notes/apply`, file));
